// Everything from OpenLayers
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import Point from "ol/geom/Point";
import { deserialize } from "flatgeobuf/lib/mjs/ol";
import { bbox } from "ol/loadingstrategy";

// Some parameters used to render the dial and bearing used to show the direction of the sign.

// Global variables used to abort API calls because the user has changed the map.
// let abortController, signal;

/**
 * @param {VectorFeatureControl} vectorFeatureControl
 * @param {string[]} urls
 * @param {function} getStyleNormal
 * @param {function} transformFeature
 * @param {number} minZoom
 * @param {string} dataProjection
 * @returns
 */
export function getFGBLayer(
  vectorFeatureControl,
  urls,
  getStyleNormal,
  transformFeature,
  minZoom = 17,
  addFeatureHandlers = [],
  dataProjection = "EPSG:4326",
) {
  const vectorSource = new VectorSource({
    minZoom: minZoom,
    strategy: bbox,

    loader: function (extent, resolution, projection, success, failure) {
      // Use flatgeobuf JavaScript API to iterate features as geojson.
      // Because we specify a bounding box, flatgeobuf will only fetch the relevant subset of data,
      // rather than the entire file.
      if (extent[0] == -Infinity) {
        return;
      }
      async function populate() {
        const rect = fgbBoundingBox(extent, projection.getCode());
        for (const url of urls) {
          let iter = deserialize(url, rect);
          try {
            const features = [];
            for await (let feature of iter) {
              feature
                .getGeometry()
                .transform(dataProjection, projection.getCode());
              feature.setProperties(
                flattenJsonProperties(feature.getProperties()),
              );
              features.push(transformFeature(feature));
            }
            success(features);
            vectorSource.addFeatures(features);
          } catch (err) {
            if (err.message != "Not a FlatGeobuf file") {
              console.error("Unable to fetch data for url " + url, err);
            }
            vectorFeatureControl.set(
              "loadErrorReason",
              err.message ?? "Unknown error",
            );
            failure();
          }
        }
      }
      populate();
    },
  });

  addFeatureHandlers.forEach((handler) => {
    vectorSource.on("featuresloadend", handler);
  });

  vectorSource.on("featuresloadstart", () => {
    vectorFeatureControl.set("loading", true);
    vectorFeatureControl.set("loadError", false);
    vectorFeatureControl.set("loadErrorReason", null);
  });
  vectorSource.on("featuresloadend", () => {
    vectorFeatureControl.set("loading", false);
  });
  vectorSource.on("featuresloaderror", () => {
    vectorFeatureControl.set("loading", false);
    vectorFeatureControl.set("loadError", true);
  });

  const vectorLayer = new VectorLayer({
    source: vectorSource,
    minZoom: minZoom,
    zIndex: 8,
    visible: false,
    style: function (feature) {
      return getStyleNormal(feature);
    },
  });

  vectorFeatureControl.set("enabled", true);
  vectorFeatureControl.on("propertychange", function (e) {
    if (e.key !== "loading" && e.key !== "tooLarge" && e.key !== "loadError") {
      propertyChangeHandler(vectorFeatureControl, vectorLayer);
    }
    if (e.key === "zoom") {
      const zoom = e.target.get("zoom");
      if (
        zoom >= vectorLayer.getMinZoom() &&
        zoom <= vectorLayer.getMaxZoom()
      ) {
        vectorFeatureControl.set("active", true);
      } else {
        vectorFeatureControl.set("active", false);
      }
    }
  });

  return vectorLayer;
}

function fgbBoundingBox(extent, projectionCode) {
  const minPoint = new Point([extent[0], extent[1]]);
  minPoint.transform(projectionCode, "EPSG:4326");

  const maxPoint = new Point([extent[2], extent[3]]);
  maxPoint.transform(projectionCode, "EPSG:4326");

  return {
    minX: minPoint.getCoordinates()[0],
    minY: minPoint.getCoordinates()[1],
    maxX: maxPoint.getCoordinates()[0],
    maxY: maxPoint.getCoordinates()[1],
  };
}

function flattenJsonProperties(properties) {
  const newEntries = Object.entries(properties).map((entry) => {
    try {
      return [entry[0], JSON.parse(entry[1])];
    } catch {
      return entry;
    }
  });
  return Object.fromEntries(newEntries);
}

/**
 * @param {VectorFeatureControl} vectorFeatureControl
 * @param {VectorLayer} vectorLayer
 */
function propertyChangeHandler(vectorFeatureControl, vectorLayer) {
  if (vectorFeatureControl.get("vectorToggle") === true) {
    vectorLayer.setVisible(true);
  } else {
    // When vectorToggle is not set, the checkbox is not checked, and so the layer is made invisible.
    vectorLayer.setVisible(false);
  }
}
